/**
 * PickingCompleteDialog - Confirmation modal when all items are picked
 *
 * Shows a routing summary of where the picked items go next:
 *  - POD garments → Printing
 *  - Pick-and-pack (PAP / "P&P") finished goods → Packing (no print step)
 *  - Anything flagged for pretreat → Pretreat shelf first
 * A mixed batch surfaces the real split (N to Printing, M to Packing) instead of
 * lumping everything under "Printing" (BLU-635).
 *
 * Design decisions:
 * - hideCloseButton + blocked overlay/Escape: worker must use CTA
 * - Auto-focus CTA via rAF per ScanErrorModal pattern
 * - text-base minimum for warehouse readability
 * - Semantic colour per destination (PRODUCTION_DESIGN.md §4.1): blue = printing,
 *   emerald = packing (ready to ship), purple = pretreat.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Printer, Droplet, Package, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ClientLabelledBadge } from '@/components/production/ClientLabelledBadge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

interface PretreatGroup {
  sku: string;
  shopifyProductType: string | null;
  size: string;
  color: string;
  quantity: number;
  fulfillmentType: string;
  /** The shirt carries a client's neck label (the pick list's badge). */
  clientLabelled?: boolean;
  clientName?: string | null;
}

interface PickingCompleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onHandOff?: () => void;
  batchNumber: string;
  totalItems: number;
  pretreatGroups: PretreatGroup[];
  pretreatCount: number;
  /** POD items (excl. pretreat) that go to the print queue next. */
  printingCount: number;
  /** Pick-and-pack items (excl. pretreat) that go to the packing station next. */
  packingCount: number;
}

type DestinationTone = 'blue' | 'emerald' | 'purple';

const TONE: Record<
  DestinationTone,
  { cardBg: string; count: string; label: string; icon: string }
> = {
  blue: {
    cardBg: 'bg-[hsl(var(--metric-shipping))]',
    count: 'text-blue-700 dark:text-blue-300',
    label: 'text-blue-600 dark:text-blue-400',
    icon: 'text-blue-600 dark:text-blue-400',
  },
  emerald: {
    cardBg: 'bg-[hsl(var(--metric-total))]',
    count: 'text-emerald-700 dark:text-emerald-300',
    label: 'text-emerald-600 dark:text-emerald-400',
    icon: 'text-emerald-600 dark:text-emerald-400',
  },
  purple: {
    cardBg: 'bg-[hsl(var(--metric-pretreat))]',
    count: 'text-purple-700 dark:text-purple-300',
    label: 'text-purple-600 dark:text-purple-400',
    icon: 'text-purple-600 dark:text-purple-400',
  },
};

/** A single destination metric card (count + label). */
function DestinationCard({
  icon: Icon,
  countLabel,
  label,
  tone,
}: {
  icon: LucideIcon;
  countLabel: string;
  label: string;
  tone: DestinationTone;
}) {
  const c = TONE[tone];
  return (
    <div className={`flex items-center gap-3 rounded-lg p-3 ${c.cardBg}`}>
      <Icon className={`h-5 w-5 flex-shrink-0 ${c.icon}`} aria-hidden="true" />
      <div>
        <p className={`text-base font-semibold ${c.count}`}>{countLabel}</p>
        <p className={`text-sm ${c.label}`}>{label}</p>
      </div>
    </div>
  );
}

export function PickingCompleteDialog({
  open,
  onOpenChange,
  onHandOff,
  batchNumber,
  totalItems,
  pretreatGroups,
  pretreatCount,
  printingCount,
  packingCount,
}: PickingCompleteDialogProps) {
  const { t } = useTranslation('production');
  const ctaRef = useRef<HTMLButtonElement>(null);

  // Auto-focus CTA when dialog opens — uses rAF for reliable focus after render
  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => {
      ctaRef.current?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [open]);

  const hasPretreat = pretreatCount > 0;
  const hasPrinting = printingCount > 0;
  const hasPacking = packingCount > 0;

  // The clean full-width "All N Items → X" card only applies when there is a
  // single destination and nothing to sort onto the pretreat shelf.
  const nonPretreatDestinations = (hasPrinting ? 1 : 0) + (hasPacking ? 1 : 0);
  const isSingleDestination = !hasPretreat && nonPretreatDestinations === 1;

  const cardCount =
    (hasPrinting ? 1 : 0) + (hasPacking ? 1 : 0) + (hasPretreat ? 1 : 0);
  const gridColsClass =
    cardCount >= 3 ? 'grid-cols-3' : cardCount === 2 ? 'grid-cols-2' : 'grid-cols-1';

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        // Block dismissal via overlay click or Escape — worker must use CTA
        if (!value) return;
        onOpenChange(value);
      }}
    >
      <DialogContent
        className="sm:max-w-lg"
        hideCloseButton
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl font-semibold text-green-600 dark:text-green-400">
            <CheckCircle className="h-8 w-8" aria-hidden="true" />
            {t('picker.pickingComplete.title')}
          </DialogTitle>
          <DialogDescription className="text-base">
            {t('picker.pickingComplete.allItems')}{' '}
            <span className="font-semibold text-foreground">{batchNumber}</span>{' '}
            {t('picker.pickingComplete.haveBeenPicked')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {isSingleDestination ? (
            hasPacking ? (
              <>
                {/* All to packing — single full-width card */}
                <div className="flex items-center gap-3 rounded-lg bg-[hsl(var(--metric-total))] p-4">
                  <Package className="h-6 w-6 text-emerald-600 dark:text-emerald-400 flex-shrink-0" aria-hidden="true" />
                  <div>
                    <p className="text-base font-semibold text-emerald-700 dark:text-emerald-300">
                      {t('picker.pickingComplete.allToPacking', { count: totalItems })}
                    </p>
                    <p className="text-sm text-emerald-600 dark:text-emerald-400">
                      {t('picker.pickingComplete.readyToPack')}
                    </p>
                  </div>
                </div>
                <p className="text-base text-muted-foreground">
                  {t('picker.pickingComplete.handOffPacking')}
                </p>
              </>
            ) : (
              <>
                {/* All to printing — single full-width card */}
                <div className="flex items-center gap-3 rounded-lg bg-[hsl(var(--metric-shipping))] p-4">
                  <Printer className="h-6 w-6 text-blue-600 dark:text-blue-400 flex-shrink-0" aria-hidden="true" />
                  <div>
                    <p className="text-base font-semibold text-blue-700 dark:text-blue-300">
                      {t('picker.pickingComplete.allToPrinting', { count: totalItems })}
                    </p>
                    <p className="text-sm text-blue-600 dark:text-blue-400">
                      {t('picker.pickingComplete.noPretreat')}
                    </p>
                  </div>
                </div>
                <p className="text-base text-muted-foreground">
                  {t('picker.pickingComplete.handOff')}
                </p>
              </>
            )
          ) : (
            <>
              {/* Destination split — one metric card per non-empty destination */}
              <div className={`grid gap-3 ${gridColsClass}`}>
                {hasPrinting && (
                  <DestinationCard
                    icon={Printer}
                    tone="blue"
                    countLabel={t('itemCount', { count: printingCount })}
                    label={t('picker.pickingComplete.printing')}
                  />
                )}
                {hasPacking && (
                  <DestinationCard
                    icon={Package}
                    tone="emerald"
                    countLabel={t('itemCount', { count: packingCount })}
                    label={t('picker.pickingComplete.packing')}
                  />
                )}
                {hasPretreat && (
                  <DestinationCard
                    icon={Droplet}
                    tone="purple"
                    countLabel={t('itemCount', { count: pretreatCount })}
                    label={t('picker.pickingComplete.pretreat')}
                  />
                )}
              </div>

              {hasPretreat ? (
                <>
                  {/* Pretreat SKU list */}
                  <div className="rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-950/30">
                    <div className="max-h-[280px] overflow-y-auto divide-y divide-purple-200 dark:divide-purple-800">
                      {pretreatGroups.map((group) => (
                        <div
                          key={`${group.sku}-${group.size}-${group.fulfillmentType}`}
                          className="flex items-center justify-between px-3 py-2"
                        >
                          <div className="flex flex-col items-start gap-0.5">
                            <span className="font-mono text-base text-foreground">
                              {group.sku}
                            </span>
                            {group.clientLabelled && <ClientLabelledBadge clientName={group.clientName} />}
                            <span className="text-sm text-muted-foreground">
                              {group.shopifyProductType || t('picker.pickingComplete.unknownType')} &middot; {group.color} &middot; {group.size}
                            </span>
                          </div>
                          <span className="text-base font-bold text-purple-700 dark:text-purple-300 pl-3">
                            &times;{group.quantity}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Info callout */}
                  <div className="rounded-lg bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 px-3 py-2.5">
                    <p className="text-sm text-purple-800 dark:text-purple-300">
                      {hasPacking
                        ? t('picker.pickingComplete.sortPretreatMixed')
                        : t('picker.pickingComplete.sortPretreat')}
                    </p>
                  </div>
                </>
              ) : (
                <p className="text-base text-muted-foreground">
                  {t('picker.pickingComplete.handOffMixed')}
                </p>
              )}
            </>
          )}
        </div>

        {/* CTA button */}
        <Button
          ref={ctaRef}
          onClick={() => {
            onHandOff?.();
            onOpenChange(false);
          }}
          className="h-16 w-full text-lg font-semibold bg-[hsl(var(--production-accent))] hover:bg-[hsl(var(--production-accent))]/90 text-[hsl(var(--production-accent-foreground))]"
        >
          {hasPretreat ? t('picker.pickingComplete.sortAndContinue') : t('picker.pickingComplete.completePicking')}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
